// A Central Inteligente IBS-CBS (000230/000231), da Pós RT — a terceira e
// última ferramenta que faltava da pós.
//
// ---------------------------------------------------------------------------
// Duas metades, e a segunda NÃO é um RAG novo
// ---------------------------------------------------------------------------
// A ferramenta tem um ACERVO (5 normas, 2.070 dispositivos, 30 temas, com a
// leitura que a casa escreveu sobre cada um) e uma IA que responde sobre ele.
//
// A IA entra no RAG que o hub já tem desde 19/08 (000068/000072): mesmo corpus,
// mesmo índice HNSW, mesmo corte por distância, mesmo histórico de conversa,
// mesmo razão de custo. O que a 000231 acrescentou foi a POLÍTICA do card —
// porque esta IA, ao contrário das outras duas, pode complementar com
// conhecimento geral (decisão de Victor em 14/09).
//
// A tela dedicada da conversa é a MESMA das outras IAs (`IaDoCurso`), com o card
// como parâmetro. Uma segunda tela de chat seria a segunda definição de "como se
// conversa com a IA aqui".
//
// ---------------------------------------------------------------------------
// Abre pela IA — decisão de Victor em 14/09
// ---------------------------------------------------------------------------
// A porta da ferramenta é a pergunta, como na origem: a caixa grande no painel
// navy, com as sugestões que vêm do BANCO (`ia_sugestao`, e não cravadas aqui —
// a cicatriz da Peric.IA sugerindo "qual o prazo da ECD?" numa pós de perícia).
// O acervo fica a um clique, nas abas.
//
// O BALÃO acompanha todas as telas de catálogo: a pergunta que o contador faz
// nasce DO QUE ELE ESTÁ LENDO, e com a conversa numa aba separada perguntar
// custaria perder o dispositivo de vista. É a mesma razão do balão do Leitor de
// Processos — e é a MESMA peça, não uma cópia.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useHub } from "../lib/dados";
import {
  lerAcervo,
  lerArtigo,
  lerArtigos,
  lerChecklist,
  mensagemDaRecusa,
  type Acervo,
  type ArtigoDaLista,
  type Checklist,
  type FichaDoArtigo,
} from "../lib/central";
import { perguntar, mensagemDaRecusa as recusaDaIa, lerSugestoes, type Fonte } from "../lib/ia";
import IaDoCurso from "./IaDoCurso";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { BalaoDoAssistente } from "../componentes/BalaoDoAssistente";
import { TextoDaIA } from "../componentes/TextoDaIA";
import {
  AbasDaCentral,
  ArvoreDaNorma,
  CartaoDeNorma,
  CartaoDeTema,
  ChipDeNorma,
  ChipDeTema,
  LinhaDeArtigo,
  Paginacao,
  SecaoDeLeitura,
  SemResultado,
  Trilha,
  rotuloCurtoDaNorma,
} from "../componentes/CentralUi";

const CARD = "central-ibs-cbs";
const POR_PAGINA = 40;

const ABAS = [
  { para: "", rotulo: "Início", exato: true },
  { para: "temas", rotulo: "Temas" },
  { para: "normas", rotulo: "Normas" },
  { para: "mudancas", rotulo: "O que muda" },
  { para: "roteiros", rotulo: "Roteiros" },
  { para: "duvidas", rotulo: "Dúvidas" },
];

export default function CentralIbsCbs() {
  const hub = useHub();
  const [params] = useSearchParams();
  const { "*": resto = "" } = useParams();
  const caminho = resto.replace(/^\/+|\/+$/g, "");

  const pronto = hub.fase === "pronto";
  const cardVitrine = pronto
    ? hub.hub.cardsDeCurso.find((c) => c.card_slug === CARD)
    : undefined;

  // O curso decide o PORTÃO, e é ele que a RPC recebe. Precedência igual à da IA
  // de curso: `?curso=` da url → o único curso deste card → nada.
  const todosOsCursos = useMemo(
    () => (pronto ? [...hub.hub.cursos, ...hub.hub.faculdade] : []),
    [pronto, hub],
  );
  const cursosPossiveis = useMemo(() => {
    if (!pronto || !cardVitrine) return [];
    return todosOsCursos.filter((c) => cardVitrine.por_cursos.includes(c.slug) && !c.trancado);
  }, [pronto, cardVitrine, todosOsCursos]);
  const cursoPedido = params.get("curso");
  const curso = useMemo(() => {
    if (cursoPedido && cursosPossiveis.some((c) => c.slug === cursoPedido)) return cursoPedido;
    if (cursosPossiveis.length === 1) return cursosPossiveis[0].slug;
    return null;
  }, [cursoPedido, cursosPossiveis]);

  const [acervo, setAcervo] = useState<Acervo | null>(null);
  const [recusa, setRecusa] = useState<string | null>(null);

  useEffect(() => {
    if (!curso) return;
    let vivo = true;
    lerAcervo(curso).then((r) => {
      if (!vivo) return;
      if (r.ok) {
        setAcervo(r.dado);
        setRecusa(null);
      } else {
        setAcervo(null);
        setRecusa(mensagemDaRecusa(r.motivo));
      }
    });
    return () => {
      vivo = false;
    };
  }, [curso]);

  // A TELA DEDICADA da conversa é a MESMA das outras IAs do hub, com o card como
  // parâmetro. Ela sai ANTES do container de leitura porque `Ferramenta.tsx` a
  // desenha em APP_SHELL (altura exata do viewport): um chat com composer no pé
  // dentro de uma coluna `max-w-6xl` com rodapé embaixo é a cicatriz do "Rodape
  // solto no flex" com o composer no lugar do rodapé.
  //
  // O gênero vai como propriedade: a tela dizia `nome === "Peric.IA" ? "à" : "ao"`,
  // e com a Central isso daria "ao Central IBS·CBS".
  if (caminho.split("/")[0] === "ia") {
    return <IaDoCurso card={CARD} nome="Central IBS·CBS" artigo="à" />;
  }

  if (hub.fase === "carregando") return <p className="p-6 text-cinza">Carregando…</p>;
  if (!pronto || !cardVitrine) {
    return (
      <p className="p-6 text-cinza">
        A Central IBS-CBS não faz parte dos cursos que você tem hoje.{" "}
        <Link to="/" className="text-link underline">
          Voltar para o início
        </Link>
      </p>
    );
  }
  if (!curso) {
    return (
      <p className="p-6 text-cinza">
        Abra a Central pela página do curso — é por ela que eu sei qual acervo carregar.
      </p>
    );
  }
  if (recusa) return <p className="p-6 text-cinza">{recusa}</p>;
  if (!acervo) return <p className="p-6 text-cinza">Carregando o acervo…</p>;

  const base = `/ferramenta/${CARD}`;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <Despacho caminho={caminho} curso={curso} acervo={acervo} base={base} />
      <BalaoDaCentral caminho={caminho} curso={curso} base={base} />
    </div>
  );
}

// ===========================================================================
// O despacho: o primeiro segmento decide a tela
// ===========================================================================
function Despacho({
  caminho,
  curso,
  acervo,
  base,
}: {
  caminho: string;
  curso: string;
  acervo: Acervo;
  base: string;
}) {
  const [primeiro, segundo] = caminho.split("/");

  if (!primeiro) return <Inicio curso={curso} acervo={acervo} base={base} caminho={caminho} />;
  if (primeiro === "temas" && segundo) {
    return <DoTema slug={segundo} curso={curso} acervo={acervo} base={base} caminho={caminho} />;
  }
  if (primeiro === "temas") return <Temas acervo={acervo} base={base} caminho={caminho} curso={curso} />;
  if (primeiro === "normas" && segundo) {
    return <DaNorma slug={segundo} curso={curso} acervo={acervo} base={base} caminho={caminho} />;
  }
  if (primeiro === "normas") return <Normas acervo={acervo} base={base} caminho={caminho} curso={curso} />;
  if (primeiro === "artigo" && segundo) {
    return <Ficha id={segundo} curso={curso} base={base} caminho={caminho} />;
  }
  if (primeiro === "mudancas") return <OQueMuda curso={curso} acervo={acervo} base={base} caminho={caminho} />;
  if (primeiro === "roteiros" && segundo) {
    return <Roteiro slug={segundo} curso={curso} base={base} caminho={caminho} />;
  }
  if (primeiro === "roteiros") return <Roteiros acervo={acervo} base={base} caminho={caminho} curso={curso} />;
  if (primeiro === "duvidas") return <Duvidas acervo={acervo} base={base} caminho={caminho} curso={curso} />;

  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <p className="mt-6 text-cinza">
        Esta seção não existe.{" "}
        <Link to={base} className="text-link underline">
          Voltar ao início da Central
        </Link>
        .
      </p>
    </>
  );
}

// ===========================================================================
// Início — a pergunta primeiro
// ===========================================================================
function Inicio({
  curso,
  acervo,
  base,
  caminho,
}: {
  curso: string;
  acervo: Acervo;
  base: string;
  caminho: string;
}) {
  const navegar = useNavigate();
  const [texto, setTexto] = useState("");
  const [sugestoes, setSugestoes] = useState<string[]>([]);

  // As sugestões vêm do BANCO, por curso. Cravá-las aqui é o defeito que a
  // Peric.IA já teve: a tela é uma só, e o texto é do produto.
  useEffect(() => {
    let vivo = true;
    lerSugestoes(curso, 4).then((s) => {
      if (vivo) setSugestoes(s);
    });
    return () => {
      vivo = false;
    };
  }, [curso]);

  const perguntarNaTelaCheia = (q: string) => {
    const limpo = q.trim();
    if (!limpo) return;
    navegar(`${base}/ia?curso=${encodeURIComponent(curso)}&q=${encodeURIComponent(limpo)}`);
  };

  const dispositivos = acervo.normas.reduce((s, n) => s + n.artigos, 0);

  return (
    <>
      <Cabine
        rotulo="Pós Reforma Tributária"
        titulo={
          <>
            Central Inteligente <span className="text-laranja-claro">IBS · CBS</span>
          </>
        }
        descricao="Pergunte com as suas palavras. A resposta vem do acervo das normas da Reforma — e diz de qual dispositivo saiu."
        fatos={
          <>
            <TileDeFato rotulo="Dispositivos" valor={dispositivos.toLocaleString("pt-BR")} apoio="com leitura da casa" />
            <TileDeFato rotulo="Normas" valor={acervo.normas.length} apoio="constituição, leis e regulamentos" />
            <TileDeFato rotulo="Temas" valor={acervo.temas.length} apoio="para navegar o acervo" />
          </>
        }
        rodape={
          <form
            data-perguntar-na-central
            onSubmit={(e) => {
              e.preventDefault();
              perguntarNaTelaCheia(texto);
            }}
            className="flex flex-col gap-2 sm:flex-row"
          >
            <label htmlFor="pergunta-da-central" className="sr-only">
              Pergunte sobre a Reforma Tributária
            </label>
            <input
              id="pergunta-da-central"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Pergunte algo sobre a Reforma Tributária…"
              // Sobre navy, cor de texto é FIXA: `text-white` com
              // `placeholder:text-navy-mute`. Token semântico aqui clareia no
              // escuro e some — a cicatriz dos cards em escada do login.
              className="min-h-12 flex-1 rounded-full border border-white/20 bg-white/10 px-5 text-white placeholder:text-navy-mute focus:border-white/50 focus:outline-none"
            />
            <button type="submit" className="pill-cta min-h-12 justify-center px-6">
              Perguntar
            </button>
          </form>
        }
      />

      {sugestoes.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-sugestoes>
          {sugestoes.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => perguntarNaTelaCheia(s)}
              className="min-h-11 rounded-full border border-borda bg-superficie px-4 text-sm text-tinta hover:bg-chip"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="mt-6">
        <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      </div>

      <section className="mt-6" aria-labelledby="normas-do-acervo">
        <h2 id="normas-do-acervo" className="font-display text-lg font-bold text-titulo">
          O acervo
        </h2>
        <p className="mt-1 text-sm text-cinza">
          Todo dispositivo tem o texto da lei e a leitura da Contabilidade Facilitada — resumo,
          explicação prática, exemplo, impactos, obrigações e riscos.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {acervo.normas.map((n) => (
            <CartaoDeNorma key={n.id} norma={n} base={base} />
          ))}
        </div>
      </section>
    </>
  );
}

// ===========================================================================
// Temas
// ===========================================================================
function Temas({ acervo, base, caminho, curso }: { acervo: Acervo; base: string; caminho: string; curso: string }) {
  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <h1 className="mt-6 font-display text-xl font-bold text-titulo">Temas</h1>
      <p className="mt-1 text-sm text-cinza">
        {acervo.temas.length} assuntos da Reforma. O número é quantos dispositivos do acervo
        tratam de cada um.
      </p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-grade-de-temas>
        {acervo.temas.map((t) => (
          <CartaoDeTema key={t.id} tema={t} base={base} />
        ))}
      </div>
    </>
  );
}

function DoTema({
  slug,
  curso,
  acervo,
  base,
  caminho,
}: {
  slug: string;
  curso: string;
  acervo: Acervo;
  base: string;
  caminho: string;
}) {
  const tema = acervo.temas.find((t) => t.slug === slug);
  const [offset, setOffset] = useState(0);
  const lista = useListaDeArtigos(curso, { tema: tema?.id ?? null, offset });

  if (!tema) {
    return (
      <>
        <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
        <p className="mt-6 text-cinza">
          Este tema não existe.{" "}
          <Link to={`${base}/temas`} className="text-link underline">
            Ver todos os temas
          </Link>
          .
        </p>
      </>
    );
  }

  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <nav className="mt-6 text-xs text-cinza">
        <Link to={`${base}/temas`} className="underline">
          Temas
        </Link>
        <span aria-hidden="true" className="mx-1.5">›</span>
        <span className="text-tinta">{tema.nome}</span>
      </nav>
      <h1 className="mt-1 font-display text-xl font-bold text-titulo">{tema.nome}</h1>
      {tema.descricao && <p className="mt-1 text-sm text-cinza">{tema.descricao}</p>}
      <p className="mt-1 text-sm text-cinza tabular-nums" data-total-do-tema>
        {tema.artigos.toLocaleString("pt-BR")} dispositivos
      </p>

      {lista.carregando ? (
        <p className="mt-6 text-cinza">Carregando…</p>
      ) : (
        <>
          <ul className="mt-4 divide-y divide-borda rounded-cf border border-borda bg-superficie">
            {lista.artigos.map((a) => (
              <LinhaDeArtigo key={a.id} artigo={a} base={base} />
            ))}
          </ul>
          <Paginacao total={lista.total} offset={offset} porPagina={POR_PAGINA} aoIr={setOffset} />
        </>
      )}
    </>
  );
}

// ===========================================================================
// Normas
// ===========================================================================
function Normas({ acervo, base, caminho, curso }: { acervo: Acervo; base: string; caminho: string; curso: string }) {
  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <h1 className="mt-6 font-display text-xl font-bold text-titulo">Normas</h1>
      <p className="mt-1 text-sm text-cinza">
        A Constituição, as leis complementares e os regulamentos do IBS e da CBS, dispositivo
        por dispositivo.
      </p>
      <div className="mt-4 grid gap-3">
        {acervo.normas.map((n) => (
          <CartaoDeNorma key={n.id} norma={n} base={base} />
        ))}
      </div>
    </>
  );
}

function DaNorma({
  slug,
  curso,
  acervo,
  base,
  caminho,
}: {
  slug: string;
  curso: string;
  acervo: Acervo;
  base: string;
  caminho: string;
}) {
  const norma = acervo.normas.find((n) => n.slug === slug);
  const [termo, setTermo] = useState("");
  const [offset, setOffset] = useState(0);
  const termoUsado = useTermoAdiado(termo);
  // A norma vem INTEIRA, e não paginada: esta tela desenha a ÁRVORE da lei, e
  // índice paginado não é índice — com o teto de 300 a LC 214 mostrava só o
  // Livro I e os outros três ficavam escondidos atrás de um "Próximos" (visto na
  // captura de 14/09). O teto do banco é 700, medido contra a maior norma do
  // acervo (620 dispositivos) — ver 000236.
  const lista = useListaDeArtigos(curso, {
    norma: norma?.id ?? null,
    termo: termoUsado || null,
    offset,
    limite: 700,
  });

  // trocar o termo volta para a primeira página: manter o offset mostraria
  // "nenhum resultado" numa busca que tem resultados na página 1
  useEffect(() => {
    setOffset(0);
  }, [termoUsado]);

  if (!norma) {
    return (
      <>
        <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
        <p className="mt-6 text-cinza">
          Esta norma não existe.{" "}
          <Link to={`${base}/normas`} className="text-link underline">
            Ver todas
          </Link>
          .
        </p>
      </>
    );
  }

  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <nav className="mt-6 text-xs text-cinza">
        <Link to={`${base}/normas`} className="underline">
          Normas
        </Link>
        <span aria-hidden="true" className="mx-1.5">›</span>
        <span className="text-tinta">{norma.nome}</span>
      </nav>
      <h1 className="mt-1 font-display text-xl font-bold text-titulo">{norma.nome}</h1>
      {norma.ementa && <p className="mt-1 text-sm text-tinta">{norma.ementa}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-cinza">
        <span className="tabular-nums">{norma.artigos.toLocaleString("pt-BR")} dispositivos</span>
        {norma.link_oficial && (
          <a
            href={norma.link_oficial}
            target="_blank"
            rel="noreferrer"
            className="text-link underline"
          >
            Ver na fonte oficial
            <span className="sr-only"> (abre em outra aba)</span>
          </a>
        )}
      </div>

      <label htmlFor="busca-na-norma" className="sr-only">
        Filtrar dispositivos desta norma
      </label>
      <input
        id="busca-na-norma"
        value={termo}
        onChange={(e) => setTermo(e.target.value)}
        placeholder="Filtrar por palavra do texto, do resumo ou do assunto…"
        className="mt-4 min-h-12 w-full rounded-full border border-borda bg-superficie px-5 text-tinta placeholder:text-cinza focus:border-acento focus:outline-none"
      />

      {/* O ESTADO da lista, declarado no DOM.
          Não é para a prova: é porque a prova não tinha como distinguir "ainda
          carregando" de "carregou vazio", e as duas desenham zero dispositivos.
          Sem esta marca ela esperava por um número que o estado errado também
          produz — e ficou intermitente três vezes no sabotador. */}
      <div
        data-lista={
          lista.carregando ? "carregando" : lista.total === 0 ? "vazia" : "pronta"
        }
        /* E a QUE TERMO este estado pertence. Sem isto a espera da prova é uma
           corrida contra os 280 ms do adiamento: entre digitar e o pedido
           partir, a tela ANTERIOR — a lei inteira, pronta e listada — já
           satisfaz "pronta e com dispositivos", então a espera volta no
           primeiro quadro e a asserção seguinte lê `carregando`. Medido em
           14/09: com a máquina ocupada o CDP demora e a prova acerta; com a
           máquina livre ela perde 3 de 3. É a cicatriz do "esperar a ROTA não
           é esperar a TELA" — a condição tem de nomear o estado NOVO, e o
           único jeito de nomeá-lo é a tela dizer a que recorte ele pertence.
           Ele sai do ESTADO, e não de `termoUsado`: a variável de render muda
           um quadro antes do efeito ligar o `carregando`, e nesse quadro o par
           (pronta, termo novo) existe sem pedido nenhum ter saído. */
        data-lista-termo={lista.termo}
      >
      {lista.carregando ? (
        <p className="mt-6 text-cinza">Carregando…</p>
      ) : lista.aviso === "termo-curto" ? (
        <p className="mt-6 text-sm text-cinza">Escreva ao menos três letras para filtrar.</p>
      ) : lista.total === 0 ? (
        <div className="mt-4">
          <SemResultado termo={termoUsado} aoLimpar={() => setTermo("")} />
        </div>
      ) : (
        <>
          {/* A contagem só aparece FILTRANDO. Sem filtro ela seria "598
              dispositivos" a cem pixels de "598 dispositivos" no cabeçalho — a
              captura de 14/09 mostrou as duas na mesma tela, e é o "conte
              quantas vezes a mesma informação aparece" do IR Pro. */}
          {termoUsado && (
            <p className="mt-4 text-sm text-cinza tabular-nums" data-total-da-norma>
              {lista.total.toLocaleString("pt-BR")} de {norma.artigos.toLocaleString("pt-BR")} casam
              com “{termoUsado}”
            </p>
          )}
          <div className="mt-4 rounded-cf border border-borda bg-superficie">
            <ArvoreDaNorma artigos={lista.artigos} base={base} buscando={!!termoUsado} />
          </div>
          {/* A paginação só aparece se a norma passar do teto — hoje nenhuma
              passa (a maior tem 620 de 700), e o componente some sozinho. Ela
              fica porque o dia em que uma norma maior entrar é justamente o dia
              em que alguém precisa ver que há mais. */}
          <Paginacao total={lista.total} offset={offset} porPagina={700} aoIr={setOffset} />
        </>
      )}
      </div>
    </>
  );
}

// ===========================================================================
// A ficha de um dispositivo
// ===========================================================================
function Ficha({
  id,
  curso,
  base,
  caminho,
}: {
  id: string;
  curso: string;
  base: string;
  caminho: string;
}) {
  const [ficha, setFicha] = useState<FichaDoArtigo | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setFicha(null);
    setErro(null);
    lerArtigo(curso, id).then((r) => {
      if (!vivo) return;
      if (r.ok) setFicha(r.dado);
      else setErro(mensagemDaRecusa(r.motivo));
    });
    return () => {
      vivo = false;
    };
  }, [curso, id]);

  if (erro) {
    return (
      <>
        <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
        <p className="mt-6 text-cinza">{erro}</p>
      </>
    );
  }
  if (!ficha) {
    return (
      <>
        <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
        <p className="mt-6 text-cinza">Carregando…</p>
      </>
    );
  }

  const { artigo, norma, temas, anterior, proximo } = ficha;
  const leitura: [string, string | null][] = [
    ["Resumo técnico", artigo.resumo_tecnico],
    ["Na prática", artigo.explicacao_pratica],
    ["Exemplo", artigo.exemplo_aplicado],
    ["Para o contador", artigo.impacto_contador],
    ["Para a empresa", artigo.impacto_empresa],
    ["Obrigações", artigo.obrigacoes],
    ["Riscos", artigo.riscos],
  ];
  const comLeitura = leitura.filter(([, v]) => v && v.trim() !== "");

  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />

      <nav className="mt-6 text-xs text-cinza">
        <Link to={`${base}/normas`} className="underline">
          Normas
        </Link>
        <span aria-hidden="true" className="mx-1.5">›</span>
        <Link to={`${base}/normas/${norma.slug}`} className="underline">
          {rotuloCurtoDaNorma(norma.tipo, norma.numero, norma.nome)}
        </Link>
      </nav>

      <header className="mt-1">
        <h1 className="font-display text-2xl font-bold text-titulo sm:text-3xl" data-rotulo-do-artigo>
          {artigo.rotulo ?? "Dispositivo"}
        </h1>
        <Trilha
          partes={[artigo.livro, artigo.titulo, artigo.capitulo, artigo.secao, artigo.subsecao]}
          className="mt-1.5"
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ChipDeNorma tipo={norma.tipo} numero={norma.numero} nome={norma.nome} />
          {temas.map((t) => (
            <ChipDeTema key={t.id} nome={t.nome} />
          ))}
        </div>
      </header>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        {/* A LEI. Fica na coluna larga e com a tipografia de leitura: é o texto
            que o contador vai citar, e citar exige ler o literal. */}
        <article>
          <h3 className="text-xs font-semibold tracking-[0.12em] text-cinza uppercase">
            Texto da lei
          </h3>
          <div
            data-texto-literal
            className="mt-2 rounded-cf border border-borda bg-superficie px-5 py-5 text-[0.95rem] leading-7 whitespace-pre-wrap text-tinta"
          >
            {artigo.texto_literal}
          </div>
          {norma.link_oficial && (
            <a
              href={norma.link_oficial}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-xs text-link underline"
            >
              Conferir na fonte oficial
              <span className="sr-only"> (abre em outra aba)</span>
            </a>
          )}
        </article>

        {/* A LEITURA DA CASA, separada e DITA. Não é a lei, e a tela não pode
            deixar dúvida sobre isso: o aluno leva a resposta para um cliente. */}
        <aside className="flex flex-col gap-3">
          <p className="text-xs text-cinza">
            Abaixo, a <strong className="text-tinta">leitura da Contabilidade Facilitada</strong>{" "}
            sobre este dispositivo. Interpretação nossa, não texto de lei.
          </p>
          {comLeitura.length === 0 ? (
            <p className="text-sm text-cinza">
              Este dispositivo ainda não tem leitura escrita. O texto da lei ao lado está completo.
            </p>
          ) : (
            comLeitura.map(([rotulo, valor], i) => (
              <SecaoDeLeitura key={rotulo} titulo={rotulo} destaque={i === 0}>
                {valor}
              </SecaoDeLeitura>
            ))
          )}
          {artigo.palavras_chave.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold tracking-[0.12em] text-cinza uppercase">
                Assuntos
              </h3>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {artigo.palavras_chave.map((p) => (
                  <ChipDeTema key={p} nome={p} />
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Andar pelo texto sem voltar à lista — a origem não tinha, e ler lei é
          justamente ir do artigo ao seguinte. */}
      <nav className="mt-6 flex flex-wrap justify-between gap-3" data-vizinhos>
        {/* Cada lado se NOMEIA. Sem isso a prova só alcançava os dois por
            posição (`a:last-of-type`), e esconder o PRÓXIMO fazia o seletor
            cair no ANTERIOR — que também troca o dispositivo, então a
            asserção passava com a navegação para a frente apagada (medido
            pelo sabotador em 14/09). É o "atributo de escopo custa nada" do
            contracheque do HUB DP. */}
        {anterior ? (
          <Link
            to={`${base}/artigo/${anterior.id}`}
            data-vizinho="anterior"
            className="min-h-11 rounded-full border border-borda px-4 py-2.5 text-sm text-tinta hover:bg-chip"
          >
            ← {anterior.rotulo ?? "Anterior"}
          </Link>
        ) : (
          <span />
        )}
        {proximo && (
          <Link
            to={`${base}/artigo/${proximo.id}`}
            data-vizinho="proximo"
            className="min-h-11 rounded-full border border-borda px-4 py-2.5 text-sm text-tinta hover:bg-chip"
          >
            {proximo.rotulo ?? "Próximo"} →
          </Link>
        )}
      </nav>

      <p className="mt-6 text-xs text-cinza">
        Conteúdo informativo, sem caráter de parecer jurídico ou contábil.
      </p>
    </>
  );
}

// ===========================================================================
// O que muda — e o RÓTULO tem de ser honesto
// ===========================================================================
// A origem chama esta tela de "Mudanças importantes" e a monta filtrando
// `importancia = 'alto'`. Medido em 14/09: **1.973 dos 2.070 são 'alto'** —
// 95,3%. Ou seja, o filtro mostra quase o acervo inteiro e o título promete uma
// curadoria que não existe.
//
// Aqui a tela mantém o PROPÓSITO (ler o acervo pelo impacto: o que o contador
// precisa fazer e o que ele arrisca) e troca o critério pelo que de fato separa,
// que é o TEMA — medido: de 7 a 914 dispositivos por tema. A escolha do tema é
// obrigatória de propósito: sem ela, esta tela seria uma lista de 2.070 itens
// com um título dizendo que são os importantes.
function OQueMuda({
  curso,
  acervo,
  base,
  caminho,
}: {
  curso: string;
  acervo: Acervo;
  base: string;
  caminho: string;
}) {
  const [params, setParams] = useSearchParams();
  const temaSlug = params.get("tema") ?? "";
  const tema = acervo.temas.find((t) => t.slug === temaSlug);
  const [offset, setOffset] = useState(0);
  const lista = useListaDeArtigos(curso, { tema: tema?.id ?? null, offset, quandoSemRecorte: "vazio" });

  useEffect(() => {
    setOffset(0);
  }, [temaSlug]);

  const escolher = (slug: string) => {
    const novo = new URLSearchParams(params);
    if (slug) novo.set("tema", slug);
    else novo.delete("tema");
    setParams(novo, { replace: true });
  };

  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <h1 className="mt-6 font-display text-xl font-bold text-titulo">O que muda</h1>
      <p className="mt-1 text-sm text-cinza">
        Escolha um tema e leia o acervo pelo IMPACTO: o que muda para o contador, o que muda
        para a empresa, as obrigações que nascem e os riscos de não cumprir.
      </p>

      <label htmlFor="tema-do-impacto" className="sr-only">
        Tema
      </label>
      <select
        id="tema-do-impacto"
        value={temaSlug}
        onChange={(e) => escolher(e.target.value)}
        className="mt-4 min-h-12 w-full rounded-full border border-borda bg-superficie px-5 text-tinta focus:border-acento focus:outline-none sm:max-w-md"
      >
        <option value="">Escolha um tema…</option>
        {acervo.temas.map((t) => (
          <option key={t.id} value={t.slug}>
            {t.nome} ({t.artigos})
          </option>
        ))}
      </select>

      {!tema ? (
        <p className="mt-6 text-sm text-cinza" data-sem-tema>
          Nenhum tema escolhido ainda — e é de propósito que a tela não abre com tudo. São{" "}
          {acervo.normas.reduce((s, n) => s + n.artigos, 0).toLocaleString("pt-BR")} dispositivos, e
          uma lista desse tamanho não é curadoria.
        </p>
      ) : lista.carregando ? (
        <p className="mt-6 text-cinza">Carregando…</p>
      ) : (
        <>
          <p className="mt-4 text-sm text-cinza tabular-nums">
            {lista.total.toLocaleString("pt-BR")} dispositivos em {tema.nome}
          </p>
          <ul className="mt-2 divide-y divide-borda rounded-cf border border-borda bg-superficie">
            {lista.artigos.map((a) => (
              <LinhaDeArtigo key={a.id} artigo={a} base={base} />
            ))}
          </ul>
          <Paginacao total={lista.total} offset={offset} porPagina={POR_PAGINA} aoIr={setOffset} />
        </>
      )}
    </>
  );
}

// ===========================================================================
// Roteiros (os checklists da origem)
// ===========================================================================
function Roteiros({ acervo, base, caminho, curso }: { acervo: Acervo; base: string; caminho: string; curso: string }) {
  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <h1 className="mt-6 font-display text-xl font-bold text-titulo">Roteiros</h1>
      <p className="mt-1 text-sm text-cinza">
        Passo a passo por perfil, com o dispositivo que fundamenta cada item.
      </p>
      {acervo.checklists.length === 0 ? (
        <p className="mt-6 text-sm text-cinza">Nenhum roteiro publicado ainda.</p>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {acervo.checklists.map((c) => (
            <Link
              key={c.id}
              to={`${base}/roteiros/${c.slug}`}
              data-roteiro={c.slug}
              className="surface porta block rounded-cf px-5 py-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-display text-base font-bold text-titulo">{c.titulo}</h3>
                <span className="shrink-0 text-xs tabular-nums text-cinza">{c.itens} itens</span>
              </div>
              {c.descricao && <p className="mt-1.5 text-sm text-tinta">{c.descricao}</p>}
              {c.publico && <p className="mt-2 text-xs text-cinza">Para: {c.publico}</p>}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

function Roteiro({
  slug,
  curso,
  base,
  caminho,
}: {
  slug: string;
  curso: string;
  base: string;
  caminho: string;
}) {
  const [dado, setDado] = useState<Checklist | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setDado(null);
    setErro(null);
    lerChecklist(curso, slug).then((r) => {
      if (!vivo) return;
      if (r.ok) setDado(r.dado);
      else setErro(mensagemDaRecusa(r.motivo));
    });
    return () => {
      vivo = false;
    };
  }, [curso, slug]);

  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <nav className="mt-6 text-xs text-cinza">
        <Link to={`${base}/roteiros`} className="underline">
          Roteiros
        </Link>
      </nav>
      {erro && <p className="mt-2 text-cinza">{erro}</p>}
      {!erro && !dado && <p className="mt-2 text-cinza">Carregando…</p>}
      {dado && (
        <>
          <h1 className="mt-1 font-display text-xl font-bold text-titulo">{dado.checklist.titulo}</h1>
          {dado.checklist.descricao && (
            <p className="mt-1 text-sm text-tinta">{dado.checklist.descricao}</p>
          )}
          {dado.checklist.publico && (
            <p className="mt-1 text-xs text-cinza">Para: {dado.checklist.publico}</p>
          )}
          {/*
            Os itens NÃO têm tique de "feito".
            "Nada de progresso sobre dado que NÃO EXISTE" tem a outra metade: o
            progresso que o ALUNO cria com um clique é legítimo (o checklist do
            ForoFácil, as aulas dos cursos bônus). Aqui ele não entra porque
            Victor não pediu, e inventar estado guardado por aluno numa tela de
            consulta é criar dado que alguém vai ter de manter.
          */}
          <ol className="mt-4 divide-y divide-borda rounded-cf border border-borda bg-superficie">
            {dado.itens.map((i, n) => (
              <li key={i.id} className="flex gap-3 px-4 py-3.5" data-item-do-roteiro={i.id}>
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-chip text-xs font-bold tabular-nums text-tinta">
                  {n + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-titulo">{i.titulo}</p>
                  {i.detalhe && <p className="mt-0.5 text-sm text-tinta">{i.detalhe}</p>}
                  {i.artigo_id && (
                    <Link
                      to={`${base}/artigo/${i.artigo_id}`}
                      className="mt-1 inline-block text-xs text-link underline"
                    >
                      {i.artigo_rotulo ?? "Ver o dispositivo"}
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </>
  );
}

// ===========================================================================
// Dúvidas (o FAQ) — e ele é DADO, não literal de tela
// ===========================================================================
function Duvidas({ acervo, base, caminho, curso }: { acervo: Acervo; base: string; caminho: string; curso: string }) {
  return (
    <>
      <AbasDaCentral abas={ABAS} atual={caminho} base={base} curso={curso} />
      <h1 className="mt-6 font-display text-xl font-bold text-titulo">Dúvidas frequentes</h1>
      <p className="mt-1 text-sm text-cinza">
        As perguntas que quase todo mundo faz. Para as outras, o assistente responde com o
        dispositivo ao lado.
      </p>
      <div className="mt-4 divide-y divide-borda rounded-cf border border-borda bg-superficie">
        {acervo.faq.map((f) => (
          <details key={f.pergunta} className="group px-4 py-3.5" data-duvida>
            <summary className="cursor-pointer list-none text-sm font-medium text-titulo marker:hidden">
              <span className="mr-2 inline-block text-cinza transition group-open:rotate-90" aria-hidden="true">
                ›
              </span>
              {f.pergunta}
            </summary>
            <p className="mt-2 pl-5 text-sm leading-relaxed text-tinta">{f.resposta}</p>
          </details>
        ))}
      </div>
      <p className="mt-4 text-sm text-cinza">
        Não achou?{" "}
        <Link to={base} className="text-link underline">
          Pergunte ao assistente
        </Link>
        .
      </p>
    </>
  );
}

// ===========================================================================
// O balão
// ===========================================================================
// Ele acompanha as telas de CATÁLOGO e some na tela dedicada da conversa —
// dois chats na mesma tela seriam dois lugares para a mesma conversa, e o aluno
// não saberia em qual está escrevendo.
function BalaoDaCentral({
  caminho,
  curso,
  base,
}: {
  caminho: string;
  curso: string;
  base: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [falas, setFalas] = useState<
    { chave: string; papel: "aluno" | "ia"; texto: string; fontes?: Fonte[] | null; aviso?: string | null }[]
  >([]);
  const conversaId = useRef<string | null>(null);
  const corpo = useRef<HTMLDivElement>(null);

  // O corpo rola por `scrollTop` do PRÓPRIO nó: `scrollIntoView` levaria a
  // página atrás junto, e o ponto do balão é a leitura continuar onde está.
  useEffect(() => {
    const el = corpo.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [falas]);

  const enviar = useCallback(
    async (p: string) => {
      const limpo = p.trim();
      if (!limpo || ocupado) return;
      setTexto("");
      setOcupado(true);
      const chave = `ia-${Date.now()}`;
      setFalas((f) => [
        ...f,
        { chave: `eu-${Date.now()}`, papel: "aluno", texto: limpo },
        { chave, papel: "ia", texto: "" },
      ]);
      const r = await perguntar({
        card: CARD,
        curso,
        pergunta: limpo,
        conversaId: conversaId.current,
        aoReceber: (acumulado) =>
          setFalas((f) => f.map((x) => (x.chave === chave ? { ...x, texto: acumulado } : x))),
      });
      setOcupado(false);
      if (!r.ok) {
        const aviso = recusaDaIa(r.motivo) ?? "Não consegui responder agora.";
        setFalas((f) => f.map((x) => (x.chave === chave ? { ...x, texto: "", aviso } : x)));
        return;
      }
      conversaId.current = r.conversaId;
      setFalas((f) =>
        f.map((x) =>
          x.chave === chave
            ? { ...x, texto: r.texto, fontes: r.fontes, aviso: recusaDaIa(r.meta.recusa) }
            : x,
        ),
      );
    },
    [curso, ocupado],
  );

  // Onde o balão NÃO aparece, e as duas razões são a mesma:
  //
  //   · na tela dedicada da conversa (`/ia`), porque dois chats na mesma tela
  //     seriam dois lugares para a mesma conversa e o aluno não saberia em qual
  //     está escrevendo;
  //   · na ENTRADA, porque ali a caixa de perguntar é a peça principal, dentro do
  //     painel navy — e a captura de 14/09 mostrou o botão "Perguntar" duas vezes
  //     na mesma tela, a 800px de distância. É o "conte quantas vezes a mesma
  //     coisa aparece" do IR Pro e dos cursos bônus.
  const primeiro = caminho.split("/")[0];
  if (primeiro === "ia" || primeiro === "") return null;

  return (
    <BalaoDoAssistente
      aberto={aberto}
      aoTrocar={setAberto}
      titulo="Assistente da Central"
      apoio="Responde pelo acervo das normas"
      selo={falas.filter((f) => f.papel === "aluno").length}
      rotuloFechado="Perguntar"
      corpoRef={corpo}
      rodape={
        <form
          data-perguntar-no-balao
          onSubmit={(e) => {
            e.preventDefault();
            void enviar(texto);
          }}
          className="flex gap-2"
        >
          <label htmlFor="pergunta-no-balao" className="sr-only">
            Pergunte sobre o que está lendo
          </label>
          <input
            id="pergunta-no-balao"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Pergunte sobre o que está lendo…"
            className="min-h-11 flex-1 rounded-full border border-borda bg-superficie px-4 text-sm text-tinta placeholder:text-cinza focus:border-acento focus:outline-none"
          />
          <button type="submit" disabled={ocupado} className="pill-cta min-h-11 px-4 disabled:opacity-60">
            {ocupado ? "…" : "Enviar"}
          </button>
        </form>
      }
    >
      {falas.length === 0 ? (
        <div className="text-sm text-cinza">
          <p>Pergunte com as suas palavras sobre o que está na tela.</p>
          <p className="mt-2">
            Para a conversa inteira, com histórico,{" "}
            <Link to={`${base}/ia?curso=${encodeURIComponent(curso)}`} className="text-link underline">
              abra o assistente
            </Link>
            .
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {falas.map((f) => (
            <div key={f.chave} data-fala={f.papel}>
              {f.papel === "aluno" ? (
                <p className="ml-auto max-w-[85%] rounded-cf bg-chip px-3 py-2 text-sm text-tinta">
                  {f.texto}
                </p>
              ) : (
                <div className="max-w-full text-sm">
                  {f.aviso && (
                    <p className="mb-1.5 rounded-cf bg-alerta-fundo px-3 py-2 text-xs text-alerta-tinta">
                      {f.aviso}
                    </p>
                  )}
                  {f.texto ? <TextoDaIA texto={f.texto} /> : !f.aviso && <p className="text-cinza">…</p>}
                  {f.fontes && f.fontes.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1.5" data-fontes>
                      {f.fontes.map((fo, i) => (
                        <li
                          key={`${fo.titulo}-${i}`}
                          className="rounded-full bg-chip px-2 py-0.5 text-[0.7rem] text-cinza"
                        >
                          {fo.titulo}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </BalaoDoAssistente>
  );
}

// ===========================================================================
// Dois ganchos
// ===========================================================================

/** A lista de artigos, com o recorte. `quandoSemRecorte: "vazio"` existe para a
 *  tela "O que muda", que NÃO deve abrir com o acervo inteiro. */
function useListaDeArtigos(
  curso: string,
  recorte: {
    norma?: string | null;
    tema?: string | null;
    termo?: string | null;
    offset: number;
    limite?: number;
    quandoSemRecorte?: "tudo" | "vazio";
  },
) {
  const { norma = null, tema = null, termo = null, offset, limite = POR_PAGINA } = recorte;
  const [estado, setEstado] = useState<{
    carregando: boolean;
    total: number;
    artigos: ArtigoDaLista[];
    aviso?: string;
    /**
     * De QUE termo é este resultado — preenchido só quando a resposta chega.
     *
     * Enquanto carrega, ele continua sendo o do resultado ANTERIOR, e é isso
     * que o torna útil: `termoUsado` é variável de render e muda no mesmo
     * quadro em que o adiamento dispara, enquanto `carregando` só vira true no
     * efeito, que roda DEPOIS. Nesse quadro a tela dizia "pronta" já com o
     * termo novo — e a espera da prova voltava em 431 ms, antes de o pedido
     * sair (medido em 14/09).
     */
    termo: string;
  }>({ carregando: true, total: 0, artigos: [], termo: "" });

  useEffect(() => {
    if (recorte.quandoSemRecorte === "vazio" && !norma && !tema && !termo) {
      setEstado({ carregando: false, total: 0, artigos: [], termo: termo ?? "" });
      return;
    }
    // O `vivo` sozinho descarta a RESPOSTA velha e deixa o PEDIDO velho
    // ocupando a fila — e na tela da norma o pedido velho traz a lei inteira
    // (620 dispositivos, ~280 kB). Quem digita esperava a busca atrás dele.
    const ctrl = new AbortController();
    let vivo = true;
    setEstado((e) => ({ ...e, carregando: true }));
    lerArtigos(curso, { norma, tema, termo, limite, offset, sinal: ctrl.signal }).then((r) => {
      if (!vivo) return;
      if (r.ok) {
        setEstado({
          carregando: false,
          total: r.dado.total,
          artigos: r.dado.artigos,
          aviso: r.dado.aviso,
          termo: termo ?? "",
        });
      } else if (r.motivo !== "cancelado") {
        setEstado({ carregando: false, total: 0, artigos: [], termo: termo ?? "" });
      }
    });
    return () => {
      vivo = false;
      ctrl.abort();
    };
    // `recorte.quandoSemRecorte` é constante por chamada — não entra nas deps
    // para não recriar o efeito a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curso, norma, tema, termo, limite, offset]);

  return estado;
}

/**
 * O termo da busca, ADIADO.
 *
 * Sem isto, cada tecla dispara uma ida ao banco — e a busca varre 6,5 MB de
 * texto gerado. 280 ms é o intervalo em que o contador termina de digitar uma
 * palavra.
 */
function useTermoAdiado(termo: string, ms = 280): string {
  const [adiado, setAdiado] = useState(termo);
  useEffect(() => {
    const t = setTimeout(() => setAdiado(termo), ms);
    return () => clearTimeout(t);
  }, [termo, ms]);
  return adiado;
}
